/**
 * Apply Quasar + body light/dark for the active shell.
 *
 * Auth pages are dark (signed-out entry) except THEMED_AUTH_PATHS, which
 * follow the preference. Editorial (docs/content) stays light. Creator, legal
 * and admin (`/admin*`) honor the product preference (default light): admin is
 * the product system in both themes, not a forced-dark shell (cas-2273c).
 * Layout:false auth pages must call useShellTheme — they never mount default.vue.
 */
import { onMounted, watch } from 'vue';
import { useQuasar } from 'quasar';
import { useThemePreference, type ThemePreference } from '~/composables/useThemePreference';
import { useShellVariant } from '~/composables/useShellVariant';
import { useNuxtApp } from '#imports';

export function applyShellTheme(
  dark: { set: (value: boolean) => void },
  options: {
    shellVariant: 'auth' | 'editorial' | 'admin' | 'creator';
    isLegalShell: boolean;
    preference: ThemePreference;
    /** An auth page in THEMED_AUTH_PATHS: follows the preference instead of forced dark. */
    followsTheme?: boolean;
  }
): void {
  if (options.shellVariant === 'auth' && !options.followsTheme) {
    dark.set(true);
    return;
  }
  const shellForcesLight = !options.isLegalShell && options.shellVariant === 'editorial';
  if (shellForcesLight) {
    dark.set(false);
    return;
  }
  dark.set(options.preference !== 'light');
}

/**
 * Mirror the theme onto <html>. Quasar only classes <body>, but <html> paints
 * whatever a page overflows (and the overscroll area), and it resolves
 * --g-bg-canvas from :root, which is dark. Putting `body--light` on <html> too
 * lets the same `.body--light` remap theme the root canvas.
 */
export function mirrorThemeOnRoot(
  root: { classList: { toggle: (token: string, force?: boolean) => boolean } },
  isDark: boolean
): void {
  root.classList.toggle('body--light', !isDark);
}

export function useShellTheme() {
  const $q = useQuasar();
  const { preference } = useThemePreference();
  const { shellVariant, isLegalShell, followsThemeAuthShell } = useShellVariant();

  const apply = () => {
    applyShellTheme($q.dark, {
      shellVariant: shellVariant.value,
      isLegalShell: isLegalShell.value,
      preference: preference.value,
      followsTheme: followsThemeAuthShell?.value ?? false
    });
    // cas-1752: mirror the theme just applied onto <html> here too, so the root never depends on
    // the watcher below re-firing (on a signed-out hard load of / it did not, and <html> was left
    // without the light class behind a light body).
    if (typeof document !== 'undefined')
      mirrorThemeOnRoot(document.documentElement, $q.dark.isActive);
  };

  // cas-84f7: the first client render must keep the server's Quasar state
  // (light by default). While hydrating,
  // the first client render must see that same state, or the class mismatch is
  // never patched (Vue leaves hydration class mismatches alone in production)
  // and a light page keeps dark controls, like white checkbox borders on white.
  // So: during hydration, apply on mount, which re-renders the controls; on a
  // client-side navigation, apply at once so layout:false pages don't flash.
  const onClient = typeof window !== 'undefined';
  const hydrating = onClient && Boolean(useNuxtApp().isHydrating);
  // Apply the route policy during SSR too, so a forced-dark auth page renders dark.
  if (!onClient || !hydrating) {
    apply();
  }
  onMounted(apply);
  // /sign-in -> /sign-up keeps the 'auth' variant but changes followsTheme.
  watch(
    followsThemeAuthShell
      ? [shellVariant, isLegalShell, preference, followsThemeAuthShell]
      : [shellVariant, isLegalShell, preference],
    apply
  );
  if (typeof document !== 'undefined') {
    // cas-1752: while hydrating, $q.dark still holds the SSR state, so an immediate mirror
    // would strip the `body--light` the theme-boot script put on <html> before the first paint.
    // Mirror immediately only on a client-side navigation; on hydration, apply() mirrors on mount.
    watch(
      () => $q.dark.isActive,
      isDark => mirrorThemeOnRoot(document.documentElement, isDark),
      { immediate: !hydrating }
    );
  }

  return { apply };
}
